import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

const MatchPredictorLeaderboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const leagueCode = searchParams.get('leagueCode');

  const [leagueData, setLeagueData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [selectedGameweek, setSelectedGameweek] = useState('overall');
  const [currentGameweek, setCurrentGameweek] = useState(null); // Start with null
  const [currentSeason, setCurrentSeason] = useState(null); // Start with null
  const [isLoading, setIsLoading] = useState(true);
  const [fixturesData, setFixturesData] = useState(null); // Start with null

  // Calculate score for a single fixture and prediction
  const calculateScore = (fixture, prediction) => {
    if (!prediction || fixture.started === false) return 0;

    const actualHome = parseInt(fixture.team_h_score);
    const actualAway = parseInt(fixture.team_a_score);
    const predictedHome = parseInt(prediction.homeScore);
    const predictedAway = parseInt(prediction.awayScore);

    if (isNaN(actualHome) || isNaN(actualAway) || isNaN(predictedHome) || isNaN(predictedAway)) return 0;

    if (actualHome === predictedHome && actualAway === predictedAway) return 7;
    if ((actualHome > actualAway && predictedHome > predictedAway) ||
        (actualHome < actualAway && predictedHome < predictedAway) ||
        (actualHome === actualAway && predictedHome === predictedAway)) {
      if (actualHome === predictedHome || actualAway === predictedAway) return 4;
      return 2;
    }
    if (actualHome === predictedHome || actualAway === predictedAway) return 1;
    return 0;
  };

  // Calculate score for the current gameweek for a single user
  const calculateCurrentGameweekScore = useCallback((userId, predictions) => {
    if (!predictions || !fixturesData || fixturesData.length === 0) return 0;

    let totalScore = 0;
    fixturesData.forEach(fixture => {
      const prediction = predictions.find(pred => pred.id === fixture.code);
      if (prediction) {
        totalScore += calculateScore(fixture, prediction);
      }
    });

    return totalScore;
  }, [fixturesData]);

  // Fetch and process leaderboard data for all members
  const fetchLeaderboardData = useCallback(async (members, startingGameweek) => {
    try {
      const usersData = await Promise.all(
        members.map(async (userId) => {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const predictionDoc = await getDoc(doc(db, 'match_predictions', `${userId}_${currentSeason}_${currentGameweek}`));
            const predictions = predictionDoc.exists() ? predictionDoc.data().predictions : null;

            const currentGameweekScore = calculateCurrentGameweekScore(userId, predictions);
            const matchPredictorScores = userData.match_predictor_scores || [];

            const scoresByGameweek = matchPredictorScores.reduce((acc, score) => {
              const [gameweek, value] = Object.entries(score)[0];
              if (parseInt(gameweek) >= startingGameweek && parseInt(gameweek) < currentGameweek) {
                acc[gameweek] = value;
              }
              return acc;
            }, {});

            if (currentGameweek >= startingGameweek) {
              scoresByGameweek[currentGameweek] = currentGameweekScore;
            }

            const totalScore = Object.values(scoresByGameweek).reduce((sum, score) => sum + score, 0);

            return {
              id: userId,
              name: userData.display_name || 'Unknown User',
              scores: scoresByGameweek,
              totalScore: totalScore
            };
          }
          return null;
        })
      );

      return usersData.filter(user => user !== null);
    } catch (error) {
      console.error("Error fetching leaderboard data:", error);
      return [];
    }
  }, [calculateCurrentGameweekScore, currentGameweek, currentSeason]);

  // Fetch current gameweek and season data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gameweekResponse, seasonResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`)
        ]);

        setCurrentGameweek(gameweekResponse.data.gameweek);
        setCurrentSeason(seasonResponse.data.season);
      } catch (error) {
        console.error("Error fetching current gameweek and season:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch fixtures data based on currentGameweek
  useEffect(() => {
    if (currentGameweek !== null) { // Only fetch if currentGameweek is available
      const fetchFixtures = async () => {
        try {
          const fixturesResponse = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/fixtures/${currentGameweek}`);
          setFixturesData(fixturesResponse.data);
        } catch (error) {
          console.error("Error fetching fixtures data:", error);
        }
      };

      fetchFixtures();
    }
  }, [currentGameweek]);

  // Fetch and process leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (currentSeason && currentGameweek !== null && fixturesData !== null) { // Ensure all dependencies are available
        setIsLoading(true);
        try {
          const leagueSnapshot = await getDocs(query(collection(db, 'leagues'), where('leagueCode', '==', leagueCode), where('type', '==', 'match')));

          if (leagueSnapshot.empty) {
            navigate('/not-found', { replace: true });
            return;
          }

          const leagueDoc = leagueSnapshot.docs[0];
          const leagueData = leagueDoc.data();
          setLeagueData(leagueData);

          const leaderboardData = await fetchLeaderboardData(leagueData.members, leagueData.startingGameweek);
          setLeaderboardData(leaderboardData);
          setIsLoading(false); // Set loading to false after data is fetched
        } catch (error) {
          console.error("Error fetching leaderboard data:", error);
          navigate('/not-found', { replace: true });
        }
      }
    };

    fetchLeaderboard(); // Call fetchLeaderboard whenever dependencies change
  }, [leagueCode, navigate, fetchLeaderboardData, currentSeason, currentGameweek, fixturesData]);

  // Handle gameweek selection change
  const handleGameweekChange = (event) => {
    setSelectedGameweek(event.target.value);
  };

  // Generate link to user's performance page
  const getPerformanceLink = (userId) => {
    const gameweek = selectedGameweek === 'overall' ? currentGameweek : selectedGameweek;
    return `/performance?uid=${userId}&openOnTab=matchPredictor&openOnGameweek=${gameweek}`;
  };

  // Display loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
      </div>
    );
  }

  // Get the score to display based on selected gameweek
  const getDisplayScore = (user) => {
    if (selectedGameweek === 'overall') {
      return user.totalScore;
    } else {
      return user.scores[selectedGameweek] || 0;
    }
  };

  // Sort leaderboard data based on selected gameweek
  const sortedLeaderboardData = [...leaderboardData].sort((a, b) => {
    if (selectedGameweek === 'overall') {
      return b.totalScore - a.totalScore;
    } else {
      return (b.scores[selectedGameweek] || 0) - (a.scores[selectedGameweek] || 0);
    }
  });

  // Framer Motion variants for table and row animations
  const tableVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
  };

  // Generate list of available gameweeks
  const availableGameweeks = currentGameweek !== null && leagueData ? 
    [...Array(Math.max(0, currentGameweek - leagueData.startingGameweek + 1))]
      .map((_, i) => i + leagueData.startingGameweek)
      .filter(gw => gw <= currentGameweek) : [];

  // Render the leaderboard
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-4xl"
    >
      <h1 className="text-4xl font-bold text-premier-purple mb-2 text-center">Match Predictor Leaderboard</h1>
      {leagueData && <h2 className="text-3xl font-semibold text-premier-pink mb-4 text-center">{leagueData.name}</h2>}
      
      {leagueData && 
        <div className="flex justify-center items-center space-x-4 mb-6 text-gray-600">
          <p className="text-sm font-medium">League Code: <span className="font-bold text-premier-purple">{leagueCode}</span></p>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <p className="text-sm font-medium">Starting Gameweek: <span className="font-bold text-premier-purple">{leagueData.startingGameweek}</span></p>
        </div>
      }

      <div className="mb-6">
        <label htmlFor="gameweek-select" className="block text-sm font-medium text-gray-700 mb-2">Select Gameweek:</label>
        <select
          id="gameweek-select"
          value={selectedGameweek}
          onChange={handleGameweekChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-premier-purple"
        >
          <option value="overall">Overall</option>
          {availableGameweeks.map((gw) => (
            <option key={gw} value={gw}>Gameweek {gw}</option>
          ))}
        </select>
      </div>

      <motion.div
        key={selectedGameweek}
        variants={tableVariants}
        initial="hidden"
        animate="visible"
        className="bg-white shadow-lg rounded-lg overflow-hidden"
      >
        <table className="w-full">
          <thead className="bg-premier-purple text-white">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Rank</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Score</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedLeaderboardData.map((user, index) => (
              <motion.tr
                key={user.id}
                custom={index}
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Link to={getPerformanceLink(user.id)} className="text-premier-purple hover:underline">
                    {user.name}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {getDisplayScore(user)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>
      <p className="mt-2 text-gray-700 text-center mb-6">Visit the <Link to="/scoring-guide?openRulesOn=match_leagues" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for a breakdown of how points are scored.</p>
      <div className="flex justify-center mt-6">
        <Link to="/leagues?selectedType=match" className="px-3 py-2 rounded-md text-sm font-medium bg-premier-purple text-white hover:bg-premier-pink hover:text-white">
          Return to Match Predictor Leagues
        </Link>
      </div>
    </motion.div>
  );
};

export default MatchPredictorLeaderboard;